import { computed, inject, Injectable, Signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { AppDownloadService } from '@ftr/api-core'
import { API_CONFIGURATION } from '@ftr/api-shared'
import { unwrapData } from '@ftr/foundation'
import { AppPaths } from '@ftr/routing-paths'
import { featureFlag } from '@ftr/ui-feature-flags'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { filter, map, startWith } from 'rxjs'
import { HelpSubMenuItem } from './help-sub-menu-flyout/item/help-sub-menu-flyout-item.component'
import { getCourtsHelpSupport, getLoggedOutTopNavHelpSupport, getPublicPortalHelpSupport } from './help-support/items'

export type HelpMenuItemsInput = { mode: 'loggedout' | 'public' } | { mode: 'courts'; courtSystemId?: string }

@Injectable({ providedIn: 'root' })
export class HelpMenuItemsProvider {
  private readonly appDownloadService = inject(AppDownloadService)
  private readonly apiConfiguration = inject(API_CONFIGURATION)
  private readonly store = inject(Store)
  private readonly router = inject(Router)

  private readonly isFtrForumEnabled = featureFlag('ftr-forum')
  private readonly isPublicNavigationEnabled = featureFlag('public-navigation')

  private readonly isLoggedIn = toSignal(this.store.select(UserState.isLoggedIn), { initialValue: false })
  private readonly courtSystems = toSignal(this.store.select(UserState.courtSystems).pipe(unwrapData()), {
    initialValue: [],
  })
  private readonly getEnabledPermissions = toSignal(this.store.select(UserState.getEnabledPermissions))
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  )

  provide(config: HelpMenuItemsInput): Signal<HelpSubMenuItem[]> {
    return computed(() => this.computeItems(config))
  }

  private computeItems(config: HelpMenuItemsInput): HelpSubMenuItem[] {
    const desktopAppDownloadUrl = this.appDownloadService.getStandaloneAppLink()
    switch (config.mode) {
      case 'loggedout':
        return getLoggedOutTopNavHelpSupport({ desktopAppDownloadUrl })
      case 'public':
        return getPublicPortalHelpSupport({
          desktopAppDownloadUrl,
          isFtrForumEnabled: this.isFtrForumEnabled(),
          courtsAppUrl: this.apiConfiguration.courtsAppUrl,
          isLoggedIn: this.isLoggedIn(),
        })
      case 'courts': {
        const isInternal = this.isInternalCourtsUser(config.courtSystemId)
        const hasCourtSystems = isInternal ? true : (this.courtSystems()?.length ?? 0) > 0
        const publicAppUrl = this.apiConfiguration.publicAppUrl
        const publicPortalUrl =
          isInternal && this.isPublicNavigationEnabled() && publicAppUrl ? publicAppUrl : undefined
        return getCourtsHelpSupport({
          desktopAppDownloadUrl,
          isFtrForumEnabled: this.isFtrForumEnabled(),
          hasCourtSystems,
          publicPortalUrl,
        })
      }
      default:
        return []
    }
  }

  private isInternalCourtsUser(courtSystemId: string | undefined): boolean {
    if (!courtSystemId) {
      return false
    }
    const url = this.currentUrl() ?? ''
    if (url.includes(`/${AppPaths.Request}/`)) {
      return false
    }
    const getPerms = this.getEnabledPermissions()
    return !!getPerms && getPerms(courtSystemId).length > 0
  }
}
